import { useCallback, useState } from 'react';

export const ACCOUNT_SETTINGS_SECTION_IDS = [
  'two-factor-auth',
  'business-information',
  'personal-information',
  'mailing-address',
  'credential-management',
] as const;

export type AccountSettingsSectionId = (typeof ACCOUNT_SETTINGS_SECTION_IDS)[number];

const STORAGE_KEY = 'mailbox.accountSettings.sections.v1';

function loadSectionState(): Record<string, boolean> {
  const defaults = Object.fromEntries(
    ACCOUNT_SETTINGS_SECTION_IDS.map((id) => [id, true]),
  ) as Record<string, boolean>;

  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return defaults;

    const parsed = JSON.parse(raw) as Record<string, boolean>;
    return { ...defaults, ...parsed };
  } catch {
    return defaults;
  }
}

function persistSectionState(state: Record<string, boolean>): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch {
    // Ignore quota / private-mode errors.
  }
}

export function useAccountSettingsSections(options?: { forceExpandSection?: AccountSettingsSectionId | null }) {
  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>(() => {
    const loaded = loadSectionState();
    if (options?.forceExpandSection) {
      return { ...loaded, [options.forceExpandSection]: true };
    }
    return loaded;
  });

  const isExpanded = useCallback(
    (sectionId: AccountSettingsSectionId) => expandedSections[sectionId] !== false,
    [expandedSections],
  );

  const toggleSection = useCallback((sectionId: AccountSettingsSectionId) => {
    setExpandedSections((prev) => {
      const next = {
        ...prev,
        [sectionId]: prev[sectionId] === false,
      };
      persistSectionState(next);
      return next;
    });
  }, []);

  const expandSection = useCallback((sectionId: AccountSettingsSectionId) => {
    setExpandedSections((prev) => {
      const next = { ...prev, [sectionId]: true };
      persistSectionState(next);
      return next;
    });
  }, []);

  return { isExpanded, toggleSection, expandSection };
}
